<!-- eslint-disable vue/no-v-html  -->
<template>
  <div
    v-if="vor"
    class="PeerReviewInformation mt-3 pb-3"
    :class="!lastItem && 'border-inverted border-bottom border-thick'"
  >
    <h2 class="mb-2 ml-2 font-weight-bold font-smaller">Now Published</h2>
    <div v-if="vor.url" class="ml-2 font-smaller">
      <a :href="vor.url" target="_blank">
        <span>
          <strong v-html="sanitizedVorLinks" />
        </span>
        <span class="d-sr-only">[opens in a new tab]</span>
        <v-icon small color="primary" aria-hidden="true">{{
          mdiOpenInNew
        }}</v-icon>
      </a>
      <div v-if="hasVorMetadata">
        <span v-if="authors" class="d-block" v-html="sanitizedAuthors" />
        <span class="text-capitalize d-block" v-html="sanitizedType" />
        <div>
          <span>
            <em
              v-if="(journalArticleType || bookChapterType) && journalName"
              v-html="sanitizedJournalName"
            />
            <span
              v-if="additionalInformation"
              v-html="sanitizedAdditionalInformation"
            />
          </span>
          <em
            v-if="proceedingsArticleType && conferenceName"
            v-html="sanitizedConferenceName"
          />
        </div>
        <div v-if="publishedDate">
          <span>{{ publishedDate }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiOpenInNew } from "@mdi/js";
import { html2text } from "~/plugins/utilities/stripHtml";

export default {
  name: "PeerReviewInformation",
  props: {
    article: { type: Object, required: true },
    lastItem: { type: Boolean, default: false },
  },
  data() {
    return {
      // Icons
      mdiOpenInNew,
    };
  },
  computed: {
    vor() {
      return this.article.vor;
    },
    hasVorMetadata() {
      return this.vor && this.vor.url && this.vor.vorDoi && this.vor.metadata;
    },
    vorLinkText() {
      return this.hasVorMetadata
        ? this.vor.metadata.title
        : "A published version of this content exists";
    },
    authors() {
      return this.hasVorMetadata
        ? this.vor.metadata.authors
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((a) => {
              if (a.lastName)
                return a.firstName
                  ? `${a.firstName} ${a.lastName}`
                  : a.lastName;
              return a.name;
            })
            .join(", ")
        : "";
    },
    journalName() {
      return this.vor.metadata.journalName;
    },
    journalArticleType() {
      return this.vor.metadata.type === "journal-article";
    },
    bookChapterType() {
      return this.vor.metadata.type === "book-chapter";
    },
    proceedingsArticleType() {
      return this.vor.metadata.type === "proceedings-article";
    },
    conferenceName() {
      return this.vor.metadata.conferenceName;
    },
    additionalInformation() {
      const { metadata } = this.vor;

      if (!metadata) return "";
      const information = [];

      if (metadata.journalName) information.push("");
      if (metadata.volume) information.push(`Volume ${metadata.volume}`);
      if (metadata.issue) information.push(`Issue ${metadata.issue}`);

      return information.join(", ");
    },
    publishedDate() {
      const publishedOnlineDate = this.vor.metadata?.publishedOnlineDate;
      const publishedPrintDate = this.vor.metadata?.publishedPrintDate;
      return publishedOnlineDate
        ? `Online publication date: ${publishedOnlineDate}`
        : publishedPrintDate
        ? `Print publication date: ${publishedPrintDate}`
        : "";
    },
    sanitizedVorLinks() {
      return html2text(this.$sanitize, this.vorLinkText);
    },
    sanitizedAuthors() {
      return html2text(this.$sanitize, this.authors);
    },
    sanitizedJournalName() {
      return html2text(this.$sanitize, this.vor.metadata.journalName);
    },
    sanitizedConferenceName() {
      return html2text(this.$sanitize, this.vor.metadata.conferenceName);
    },
    sanitizedAdditionalInformation() {
      return html2text(this.$sanitize, this.additionalInformation);
    },
    sanitizedType() {
      const type = this.vor.metadata.type?.replace(/-/g, " ");
      return html2text(this.$sanitize, type);
    },
  },
};
</script>
